// router
import Router from 'bpm-router';

// helpers
import { bindEvents, getRootOffsetBounds } from '../../../assets/js/helpers';

// bits
import CursorExpand from '../../../bits/cursors/expand/script';

// controllers
import AnimationController from '../../../assets/js/controller/animation';
import CursorController from '../../../assets/js/controller/cursor';
import ResizeController from '../../../assets/js/controller/resize';

// dom selector (prefixed by data-)
const selector = 'video-banner';

export default class VideoBanner {

	constructor(section) {
		this.el = {
			section,
			poster: section.querySelector(`.${selector}__poster`),
			container: section.querySelector(`.${selector}__poster-image`),
			cta: section.querySelector(`.${selector}__cta`),

			// external elements
			body: document.querySelector('body'),
			videoPlayer: document.querySelector('.video-player'),
			fixed: document.querySelector('.fixed')
		};

		// class variable groups

		this.bounds = {
			winWidth: null,
			winHeight: null,
			container: {}
		};

		this.config = {
			delays: {
				interaction: 600,
				intro: 300,
				replace: 300
			}
		};

		this.flags = {
			isCursorActive: false,
			isInteractionReady: true
		};

		this.controllers = {
			animateCursor: null,
			resize: null
		};

		this.timeouts = {
			deactivate: null,
			player: null,
			replace: null
		};

		// class variables
		this.cursor = new CursorExpand(this.el.cta);

		// bind events to class scope
		this.events = bindEvents(this);

		// LET'S MOSEY
		this.init();
	}

	static get selector() {
		return selector;
	}

	init() {
		this.measure();
		this.addEventListeners();

		// init cursor

		this.cursor.start();
		this.centerCursor();
		this.activateCursor();
	}

	// calculate bounds

	measure() {
		this.bounds.winWidth = window.innerWidth;
		this.bounds.winHeight = window.innerHeight;
		this.bounds.container = getRootOffsetBounds(this.el.container);
	}

	// event listeners

	addEventListeners() {
		this.controllers.animateCursor = AnimationController.set({
			update: this.updateCursor.bind(this),
			render: this.renderCursor.bind(this)
		});

		this.controllers.resize = ResizeController.set({
			update: () => { this.measure(); },
			resize: () => {}
		});

		this.el.poster.addEventListener('click', this.events._onPosterClick);

		if (this.el.videoPlayer) {
			this.el.videoPlayer.addEventListener('hide', this.events._onPlayerHide);
		}
	}

	removeEventListeners() {
		AnimationController.clear(this.controllers.animateCursor);
		ResizeController.clear(this.controllers.resize);

		this.el.poster.removeEventListener('click', this.events._onPosterClick);

		if (this.el.videoPlayer) {
			this.el.videoPlayer.removeEventListener('hide', this.events._onPlayerHide);
		}
	}

	// listener methods

	_onPosterClick(e) {
		e.preventDefault();
		this.expand();
	}

	_onCloseClick(e) {
		e.preventDefault();
	}

	_onPlayerHide() {
		clearTimeout(this.timeouts.player);
		this.timeouts.player = setTimeout(() => this.shrink(), 300);
	}

	// video player expand / collapse

	expand() {
		if (this.el.cta.classList.contains('is-video')) {
			let scale = this.bounds.winWidth / this.bounds.container.width;
			if (scale * this.bounds.container.height < this.bounds.winHeight) scale = this.bounds.winHeight / this.bounds.container.height;

			// const translateX = -(this.bounds.container.left) - (scale * this.bounds.container.width - this.bounds.winWidth) / 2;
			// const translateY = this.bounds.containerOffsetTop - window.scrollY;

			if (this.el.fixed) this.el.fixed.classList.add('is-hidden');

			this.el.body.classList.add('is-no-scroll');
			this.el.section.classList.add('is-expanded');
			// this.el.container.style.transform = `translate3d(${translateX}px, ${-translateY}px, 0) scale3d(${scale}, ${scale}, 1)`;

			if (this.el.videoPlayer) this.el.videoPlayer.dispatchEvent(new CustomEvent('show'));

			this.showPlayer();

		} else {
			Router.navigate(this.el.cta.getAttribute('href'));
		}
	}

	shrink() {
		if (this.el.fixed) this.el.fixed.classList.remove('is-hidden');

		this.el.body.classList.remove('is-no-scroll');
		this.el.section.classList.remove('is-expanded');
		this.el.container.style.transform = null;
	}

	showPlayer() {
		clearTimeout(this.timeouts.player);
		this.timeouts.player = setTimeout(() => {
			this.el.videoPlayer.classList.add('is-shown');
		}, 0);
	}

	// button / cursor

	checkCursor() {
		if (this.flags.isInteractionReady) {
			if (this.flags.isCursorOver) {
				this.activateCursor();
			} else {
				this.deactivateCursor();
			}
		}
	}

	activateCursor() {
		if (!this.flags.isCursorActive && this.flags.isInteractionReady) {
			this.flags.isCursorActive = true;

			if (this.el.section.classList.contains('is-cursor-centered')) {
				this.cursor.slow();
			}

			this.el.section.classList.remove('is-cursor-centered');
			this.el.cta.classList.add('is-active');

			// prevent delayed deactivate / replace
			this.clearCursorTimeouts();
		}
	}

	deactivateCursor() {
		if (this.flags.isCursorActive) {
			this.flags.isCursorActive = false;
			this.el.cta.classList.remove('is-active');

			// after cursor has been deactivated, replace in center of banner
			this.clearCursorTimeouts();

			this.timeouts.deactivate = setTimeout(() => {
				this.replaceCursor();
			}, this.config.delays.replace);
		}
	}

	replaceCursor() {
		// set cursor back to original centered position
		this.clearCursorTimeouts();
		this.flags.isInteractionReady = false;

		// move to center
		this.centerCursor();

		// reset cursor interaction state
		this.timeouts.replace = setTimeout(() => {
			this.el.section.classList.add('is-cursor-centered');
			this.el.cta.classList.add('is-active');
		}, this.config.delays.intro);

		this.timeouts.replace = setTimeout(() => {
			this.flags.isInteractionReady = true;
			this.checkCursor();
		}, this.config.delays.interaction);
	}

	clearCursorTimeouts() {
		clearTimeout(this.timeouts.deactivate);
		clearTimeout(this.timeouts.intro);
		clearTimeout(this.timeouts.replace);
	}

	updateCursor() {
		const { scrollX, scrollY } = CursorController.coords;
		let isIn = false;
		if (scrollX > this.bounds.container.left && scrollX < this.bounds.container.right
			&& scrollY > this.bounds.container.top && scrollY < this.bounds.container.bottom) {
			isIn = true;
		}

		this.flags.isCursorOver = isIn;

		if (isIn || (!isIn && this.flags.isCursorActive)) {
			return true;
		}
	}

	renderCursor() {
		if (this.flags.isInteractionReady && this.flags.isCursorOver) {
			this.cursor.moveTo(CursorController.coords.scrollX - this.bounds.container.left, CursorController.coords.scrollY - this.bounds.container.top);
		}

		this.checkCursor();
	}

	centerCursor() {
		this.cursor.moveTo(this.bounds.container.width / 2, this.bounds.container.height / 2 - this.cursor.offset.y);
	}

	// destroy

	destroy() {
		if (this.timeouts.player) {
			clearTimeout(this.timeouts.player);
		}

		this.removeEventListeners();
	}

}
